import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DataService } from '../data.service';
import { ActionType, Module, ROLES } from '../../models/enumeration-list.model';
import { AIMSROUTES } from '../../../app-routing.module';
import { AbcLoginComponentInfo } from '@barthauer/aptis-authentication';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  constructor(
    private dataService: DataService,
    private router: Router,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.dataService.isArchived.set(false);
    let alreadyRedirected = false;
    const requiredRoles = route.data['roles'] as string[];
    const perm = route.data['permsision'] as ActionType;
    const module = Module[this.dataService.getBasePath(route.routeConfig.path)];

    return this.dataService.prepareUserData().pipe(
      map((userData)=>{
        if(userData) this.dataService.isArchived.set(userData._profileInfo[0].Active===false);
        return this.dataService.isArchived() ?  null : userData
      }),
      tap((userData) => {
        if (userData) {
          this.dataService.setProfileInformations(userData);
        }
      }),
      switchMap((userData) => {
        if(this.dataService.isArchived()){
          this.router.navigate([AbcLoginComponentInfo.ROUTE_PATH]);
          return of(false);
        }

        if (userData) {
          this.dataService.isSuperAdmin = userData._profileInfo[0].Function === ROLES.SUPER_ADMIN;
        }

        // **Super Admin logic**
        if (this.dataService.isSuperAdmin) {
          if (requiredRoles?.includes(ROLES.SUPER_ADMIN)) {
            return of(true);
          } else if (route.routeConfig.path === AIMSROUTES.COMPANIES) {
            this.router.navigate([AIMSROUTES.COMPANIES_LIST]);
            return of(false);
          } else {
            this.router.navigate([AbcLoginComponentInfo.ROUTE_PATH]);
            return of(false);
          }
        }


        if (route.routeConfig.path === AIMSROUTES.COMPANIES) {
          const profileAddressId = sessionStorage.getItem('profileAddressId');
          const profileId = sessionStorage.getItem('profileId');


          if (profileAddressId && profileId) {
            return this.dataService.loadUserRole(profileAddressId, profileId).pipe(
              map(() => true),
              catchError(() => of(true))
            );
          }


          return of(true);
        }

        // **Other routes - must have company selected**
        const profileAddressId = sessionStorage.getItem('profileAddressId');
        const profileId = sessionStorage.getItem('profileId');

        if (!profileAddressId || !profileId) {
          if (!alreadyRedirected) {
            alreadyRedirected = true;
            this.router.navigate([AIMSROUTES.COMPANIES]);
          }
          return of(false);
        }


        return this.dataService.loadUserRole(profileAddressId, profileId).pipe(
          map((userRoleData) => {
            const { roles, permissions } = userRoleData || {};

            if (roles?.length) {
              this.dataService.setRole(roles);


              if (route.routeConfig?.path === 'notification') {
                return true;
              }


              if (this.dataService.hasPermission(module, perm)) {
                return true;
              }


              if (!alreadyRedirected) {
                alreadyRedirected = true;
                this.dataService.unothorized = true;
                this.router.navigate(['/unauthorized']);
              }
              return false;
            } else {

              if (!alreadyRedirected) {
                alreadyRedirected = true;
                this.router.navigate(['/login']);
              }
              return false;
            }
          }),
          catchError((error) => {
            if (!alreadyRedirected) {
              alreadyRedirected = true;
              this.router.navigate(['/unauthorized']);
            }
            return of(false);
          })
        );
      }),
      catchError((error) => {
        if (!alreadyRedirected) {
          alreadyRedirected = true;
          this.router.navigate(['/login']);
        }
        return of(false);
      })
    );
  }
}

